<template>
    <component
        :is="componentType"
        v-bind="componentAttrs"
        :class="buttonClasses"
        @click="handleClick"
    >
        <span :class="[
            'tw:flex tw:items-center tw:gap-2 tw:w-full',
            align === 'left' ? 'tw:justify-start tw:text-left' : 'tw:justify-center tw:text-center'
        ]"
        >
            <i v-if="loading" class="fas fa-spinner fa-spin tw:flex tw:items-center"></i>
            <i v-else-if="icon" class="tw:flex tw:items-center" :class="icon"></i>
            <slot></slot>
        </span>
    </component>
</template>

<script setup>
    import { computed } from 'vue';

    const props = defineProps({
        id: String,
        dataCy: String,
        disabled: Boolean,
        loading: Boolean,
        customClass: String,
        icon: String,
        align: {
            type: String,
            default: 'center',
            validator: v => ['left', 'center'].includes(v)
        },
        ignoreBaseClasses: {
            type: Boolean,
            default: false
        },
        as: {
            type: String,
            default: 'button',
            validator: v => ['button', 'router-link', 'a'].includes(v)
        },
        type: {
            type: String,
            default: 'button',
            validator: v => ['button', 'submit', 'reset'].includes(v)
        },
        to: {
            type: [String, Object],
            default: null
        },
        href: {
            type: String,
            default: null
        },
        target: {
            type: String,
            default: null
        },
        variant: {
            type: String,
            default: null,
            validator: v => ['default', 'pagination', 'pagination-active', 'primary', 'success', 'secondary', 'danger', 'ghost', 'border', 'border-active', 'border-error'].includes(v)
        }
    });

    const emit = defineEmits(['click']);

    const componentType = computed(() => {
        if (props.as === 'router-link' && props.disabled) {
            return 'button'
        }
        if (props.as === 'a' && props.disabled) {
            return 'button'  
        }
        return props.as
    })

    /**
     * Atributos que se pasan al elemento/componente raíz.
     *
     * OJO: cada clave se OMITE cuando no aplica, en vez de pasarla como
     * `undefined`. Un `undefined` explícito llega como atributo del padre y el
     * fallthrough de Vue lo aplica al `<a>` del RouterLink con
     * `removeAttribute('href')`, borrando el href que el propio RouterLink
     * acababa de pintar. Se veía al re-renderizar la tarjeta (p. ej. al
     * filtrar el listado): el clic seguía navegando, pero se perdían abrir en
     * otra pestaña, el clic central y el rol de enlace para lectores de
     * pantalla.
     */
    const componentAttrs = computed(() => {
        const attrs = {
            id: props.id,
            'data-cy': props.dataCy
        };

        if (componentType.value === 'router-link') {
            attrs.to = props.to;
        } else if (componentType.value === 'a') {
            attrs.href = props.href;
            attrs.target = props.target;
        } else {
            attrs.type = props.type;
            if (props.disabled || props.loading) attrs.disabled = true;
        }

        return attrs;
    });

    const variantClasses = computed(() => {
        if (props.ignoreBaseClasses) return '';

        const map = {
            'default':           'tw:flex tw:items-center tw:px-4 tw:py-2 tw:text-sm tw:text-slate-200 tw:group tw:rounded-sm tw:hover:bg-[#0069d9] tw:hover:border-[#0062cc]',
            'pagination':        'tw:bg-white tw:flex tw:items-center tw:justify-center tw:min-w-8 tw:h-8 tw:px-2 tw:text-xs tw:leading-4 tw:rounded tw:border tw:border-gray-300 tw:text-gray-500 tw:hover:border-gray-500',
            'pagination-active': 'tw:flex tw:items-center tw:justify-center tw:min-w-8 tw:h-8 tw:px-2 tw:text-xs tw:leading-4 tw:rounded tw:border tw:bg-blue-500 tw:text-white tw:border-blue-800',
            'primary':           'tw:bg-blue-500 tw:text-white tw:px-4 tw:py-2 tw:text-sm tw:rounded tw:hover:bg-black',
            'success':           'tw:bg-green-600 tw:text-white tw:px-4 tw:py-2 tw:text-sm tw:rounded tw:hover:bg-green-700',
            'secondary':         'tw:bg-gray-500 tw:text-white tw:px-4 tw:py-2 tw:text-sm tw:rounded tw:hover:bg-gray-800',
            'danger':            'tw:bg-red-500 tw:text-white tw:flex tw:items-center tw:px-4 tw:py-2 tw:text-sm tw:rounded tw:hover:bg-red-800',
            'ghost':             'tw:bg-transparent tw:text-gray-500 tw:px-4 tw:py-2 tw:text-sm tw:rounded tw:hover:bg-gray-100',
            'border':            'tw:border tw:border-gray-300 tw:bg-white tw:text-gray-700 tw:rounded',
            'border-active':     'tw:border tw:border-blue-500 tw:bg-blue-50 tw:text-blue-600 tw:rounded',
            'border-error':      'tw:border tw:border-red-500 tw:text-red-600 tw:bg-transparent tw:rounded',
        };
        return props.variant ? map[props.variant] : map['default'];
    });

    const buttonClasses = computed(() => {
        const baseClasses = props.ignoreBaseClasses ? '' : 'button'
        const disabledClasses = (props.disabled || props.loading) ? 'tw:opacity-50 tw:cursor-not-allowed tw:pointer-events-none' : ''
        return [baseClasses, variantClasses.value, disabledClasses, props.customClass].filter(Boolean).join(' ')
    })

    const handleClick = event => {
        if (props.disabled || props.loading) {
            event.preventDefault()
            event.stopPropagation()
            return
        }

        emit('click', event)
    }
</script>